import { useAppDispatch } from 'app/hooks';
import { ProjectSettingCodeType } from 'data/types';
import { updateProjectSetting } from 'features/project';
import { isNotEmpty } from 'utils/helpers';
import { getProjectSetting } from 'utils/project';
import AttributeComponent from './attribute';

type ProjectSettingProps = {
  code: ProjectSettingCodeType;
  showProgress?: boolean;
  showLabel?: boolean;
  required?: boolean;
  debounce?: number;
  disabled?: boolean;
  autoFocus?: boolean;
  onChange?: (value?: string) => void;
  width?: number;
  preventDeselection?: boolean;
  // Displayed when no value is stored (e.g. a derived default). NEVER
  // persisted — updateProjectSetting still fires only on user change, and
  // `completed` stays based on the stored value.
  fallbackValue?: any;
};

export default function ProjectSetting({
  code,
  showProgress = false,
  showLabel = false,
  required,
  debounce,
  disabled,
  autoFocus,
  onChange,
  width,
  preventDeselection,
  fallbackValue
}: ProjectSettingProps) {
  const dispatch = useAppDispatch();
  const setting = getProjectSetting(code);

  if (!setting) {
    return <div className='text-red-500'>{code} not found!</div>;
  }

  const handleChange = (value: any) => {
    let convertedValue: any = value;

    if (setting.type === 'checkbox') {
      convertedValue = value === true ? true : undefined;
    }

    dispatch(
      updateProjectSetting({
        attributeId: setting.id,
        value: convertedValue
      })
    );

    onChange?.(convertedValue);
  };

  let displayValue: any = setting.value;

  if (setting.type === 'checkbox') {
    displayValue = setting.value === true;
  }

  if (fallbackValue !== undefined && !isNotEmpty(setting.value)) {
    displayValue = fallbackValue;
  }

  return (
    <AttributeComponent
      {...setting}
      value={displayValue}
      showProgress={showProgress}
      showLabel={showLabel}
      completed={isNotEmpty(setting.value)}
      required={required}
      debounce={debounce}
      disabled={disabled}
      autoFocus={autoFocus}
      onChange={handleChange}
      width={width}
      preventDeselection={preventDeselection}
    />
  );
}
